import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useCompanies, useRoles } from '@adsk/alloy-provider';
import { useAssignableProjectProducts } from '@adsk/acs-admin-ui-components-hooks';
import ProjectMemberFilterPanel from './component';
import { FILTER_KEYS } from './constants';
import { useMapByKey, useMemberStatuses } from './hooks';
import { isFilterCleared } from './utils';

const ProjectMemberFilterPanelContainer = ({
  isEmsAccount,
  onClose,
  open,
  onFilterUpdate,
  selectedFilters,
  enableProductFilter,
  enableStatusFilter,
}) => {
  const {
    data: products,
    productsMapByKey,
    loading: loadingProducts,
  } = useAssignableProjectProducts(enableProductFilter && !isEmsAccount);
  const { data: companies } = useCompanies();
  const { data: companiesMapById, loading: loadingCompaniesMap } = useMapByKey(
    useCompanies,
    'id'
  );
  const { data: roles } = useRoles();
  const { data: rolesMapById, loading: loadingRolesMap } = useMapByKey(
    useRoles,
    'id'
  );
  const { options: memberStatuses, optionsMapById: memberStatusesMapById } =
    useMemberStatuses();

  const selectedCompany = useMemo(() => {
    if (selectedFilters[FILTER_KEYS.COMPANY_ID] && !loadingCompaniesMap) {
      return companiesMapById[selectedFilters[FILTER_KEYS.COMPANY_ID]];
    }
    return null;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters[FILTER_KEYS.COMPANY_ID], loadingCompaniesMap]);

  const selectedRoles = useMemo(() => {
    if (selectedFilters[FILTER_KEYS.ROLE_IDS] && !loadingRolesMap) {
      return selectedFilters[FILTER_KEYS.ROLE_IDS].map(
        (id) => rolesMapById[id]
      );
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters[FILTER_KEYS.ROLE_IDS], loadingRolesMap]);

  const selectedProducts = useMemo(() => {
    if (selectedFilters[FILTER_KEYS.PRODUCTS] && !loadingProducts) {
      return selectedFilters[FILTER_KEYS.PRODUCTS].map(
        (key) => productsMapByKey[key]
      );
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters[FILTER_KEYS.PRODUCTS], loadingProducts]);

  const selectedStatuses = useMemo(() => {
    if (selectedFilters[FILTER_KEYS.STATUS]) {
      return selectedFilters[FILTER_KEYS.STATUS].map(
        (id) => memberStatusesMapById[id]
      );
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters[FILTER_KEYS.STATUS]]);

  const handleFilterChange = useCallback(
    (filterKey, filterValue) => {
      if (isFilterCleared(filterValue)) {
        onFilterUpdate({ ...selectedFilters, [filterKey]: undefined });
      } else {
        onFilterUpdate({ ...selectedFilters, [filterKey]: filterValue });
      }
    },
    [onFilterUpdate, selectedFilters]
  );

  const modifiedRoles = useMemo(() => {
    if (!roles.length) return [];

    const updatedRoles = roles.map((role) => {
      if (role.status === 'inactive') {
        return { ...role, name: `${role.name} (Removed)` };
      }
      return role;
    });

    return updatedRoles;
  }, [roles]);

  return (
    <ProjectMemberFilterPanel
      companies={companies}
      enableProductFilter={enableProductFilter && !isEmsAccount}
      enableCompanyFilter={!isEmsAccount}
      enableStatusFilter={enableStatusFilter}
      isLoadingCompanies={loadingCompaniesMap}
      isLoadingProducts={loadingProducts}
      isLoadingRoles={loadingRolesMap}
      memberStatuses={memberStatuses}
      onClose={onClose}
      onFilterChange={handleFilterChange}
      open={open}
      products={products}
      roles={modifiedRoles}
      selectedCompany={selectedCompany}
      selectedProducts={selectedProducts}
      selectedRoles={selectedRoles}
      selectedStatuses={selectedStatuses}
    />
  );
};
ProjectMemberFilterPanelContainer.propTypes = {
  enableProductFilter: PropTypes.bool,
  enableStatusFilter: PropTypes.bool,
  isEmsAccount: PropTypes.bool,
  onClose: PropTypes.func,
  onFilterUpdate: PropTypes.func,
  open: PropTypes.bool,
  selectedFilters: PropTypes.shape({
    companyId: PropTypes.string,
    products: PropTypes.arrayOf(PropTypes.string),
    roleIds: PropTypes.arrayOf(PropTypes.string),
    status: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

ProjectMemberFilterPanelContainer.defaultProps = {
  enableProductFilter: true,
  enableStatusFilter: true,
  isEmsAccount: false,
  onClose: () => {},
  open: false,
  onFilterUpdate: () => {},
};

export default ProjectMemberFilterPanelContainer;
